/**
 * Operator Store
 *
 * Manages the canonical Stream operator list and the operator currently
 * signed in to this Stream client. The operator selection drives which
 * GCP-Secret-Manager-stored API key the Rust layer fetches at startup
 * (and on switch), so backend telemetry can attribute every action to a
 * specific human.
 *
 * Architecture:
 * - The list comes from the Rust layer via `list_operators` (hardcoded at
 *   build time in `src-tauri/src/services/auth.rs::OPERATORS`).
 * - Persistence is intentionally narrow: only the selected operator's
 *   `consumer_id` is stored. The full `Operator` is re-resolved from
 *   `available` after `loadOperators()` completes.
 * - If a persisted `consumer_id` no longer appears in the list (e.g. an
 *   operator removed in a build), the selection is dropped silently — the
 *   user lands on the splash screen and picks again.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { listOperators, getStaffProfile, type Operator, type StaffProfile } from '@/lib/tauri'
import { IS_TAURI } from '@/features/auth/lib/staff-session'
import { api, ApiClientError } from '@/lib/api'
import { hasScope as checkScope } from '@/lib/auth/scope-check'

/** The last loaded staff profile, or null before `loadStaffProfile()` runs. */
export type { StaffProfile }

interface OperatorState {
  /** Currently selected operator (null until they pick one on the splash). */
  selected: Operator | null
  /** All operators known by the Rust layer. Resolved by `loadOperators()`. */
  available: Operator[]
  /** True once `listOperators()` or `loadStaffProfile()` has resolved at least once. */
  isLoaded: boolean
  /** Tauri / network error, if any (e.g. command failed). */
  loadError: string | null

  /**
   * Set when `loadStaffProfile()` resolves with a domain-level "no account"
   * or "no key provisioned" condition — distinct from a loadError (network /
   * command failure). The OperatorSelector renders different UI for each case.
   *
   * - `'no_account'`  — Google token is valid but no staff record exists.
   * - `'no_key'`      — Staff record exists but consumer_id is empty/missing.
   * - `null`          — No error; a selectable profile is in `available[0]`.
   */
  profileError: 'no_account' | 'no_key' | null

  /**
   * The resolved staff profile from the last `loadStaffProfile()` call.
   * Populated on success (even when consumer_id is absent).
   */
  staffProfile: StaffProfile | null

  /**
   * The current operator's effective scope set, loaded from GET /v1/me/scopes
   * after the API client is configured. Empty array until `loadScopes()` resolves.
   * Wildcard admin operators carry ["*"]; role-based operators carry their bundle.
   * (Task 3552c)
   */
  scopes: string[]
  /**
   * True once `loadScopes()` has resolved at least once. When false, ScopeGate
   * renders PageLoadingState rather than AccessDeniedPanel to avoid a flash of
   * denial while scopes are being fetched.
   */
  scopesLoaded: boolean

  /**
   * Resolve the signed-in Google identity to a staff profile via
   * `GET /v1/auth/staff/me` (Tauri command `get_staff_profile`). Sets
   * `available` to a single synthetic Operator derived from the profile, or
   * sets `profileError` for the "no account" / "no key" cases.
   *
   * This replaces `loadOperators()` as the primary splash-screen load path.
   * `loadOperators()` is retained for compatibility but is no longer called
   * from the OperatorSelector.
   */
  loadStaffProfile: (options?: { force?: boolean }) => Promise<void>

  /**
   * Fetch the operator list from the Rust layer. Idempotent — repeat calls
   * after a successful load no-op unless `force` is true. Reconciles the
   * persisted `selected.consumer_id` against the freshly loaded list.
   *
   * @deprecated Prefer `loadStaffProfile()` which resolves the identity
   * dynamically from the signed-in Google account.
   */
  loadOperators: (options?: { force?: boolean }) => Promise<void>
  /**
   * Select an operator. Persists the selection. The caller is responsible
   * for triggering an environment-store reload so API keys swap to the new
   * operator's secrets.
   */
  selectOperator: (op: Operator) => void
  /**
   * Clear the selection (used by the "Switch operator" row in settings).
   * Does NOT clear `available` — the splash should re-render immediately
   * without an extra Tauri round-trip.
   */
  clearOperator: () => void

  /**
   * Fetch the current operator's effective scopes from GET /v1/me/scopes.
   * Must be called after the API client is configured (i.e. after environment
   * store initialises). Idempotent — repeat calls no-op unless `force` is true.
   * (Task 3552c)
   */
  loadScopes: (options?: { force?: boolean }) => Promise<void>

  /**
   * Returns true if the current operator's scope set satisfies the required
   * scope(s). Uses the same matching logic as the server's Scope.HasScope:
   * - "*" satisfies anything
   * - "case:*" satisfies "case:read", "case:write", etc.
   * - Exact match
   * - For arrays, ALL must be satisfied (AND semantics)
   *
   * Consumers should prefer this over reading `scopes` directly.
   */
  hasScope: (required: string | string[]) => boolean
}

/** Derive 1–2 uppercase initials from a display name or email address. */
function initials(nameOrEmail: string): string {
  const name = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail
  const words = name.split(/[\s._-]+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/**
 * Browser-only: resolve the staff profile from the gateway using the ID token
 * held by the auth store. Imported lazily to avoid a module cycle
 * (auth store -> api client -> ... -> operator store).
 */
async function fetchBrowserStaffProfile(): Promise<StaffProfile> {
  const { useGoogleAuthStore } = await import('@/features/auth/stores/google-auth-store')
  const { fetchStaffProfile } = await import('@/features/auth/lib/staff-session')
  const idToken = useGoogleAuthStore.getState().idToken
  if (!idToken) {
    throw new Error('not_signed_in')
  }
  return fetchStaffProfile(idToken)
}

export const useOperatorStore = create<OperatorState>()(
  persist(
    (set, get) => ({
      selected: null,
      available: [],
      isLoaded: false,
      loadError: null,
      profileError: null,
      staffProfile: null,
      scopes: [],
      scopesLoaded: false,

      loadStaffProfile: async (options) => {
        const force = options?.force ?? false
        if (!force && get().isLoaded) return

        set({ loadError: null, profileError: null, staffProfile: null })

        try {
          // Desktop resolves the profile through the Rust layer, which holds
          // the refresh token. The browser has no Rust layer — it calls the
          // same gateway endpoint directly with the ID token from sign-in
          // (Task 4092). Without this branch, `invoke` is undefined in a
          // browser and boot fails with "Cannot read properties of undefined".
          const profile = IS_TAURI
            ? await getStaffProfile()
            : await fetchBrowserStaffProfile()

          // Build a synthetic Operator from the profile so the downstream
          // continue-flow (getEnvironmentConfigs + environment store) works
          // unchanged.
          if (!profile.consumer_id) {
            // Account exists but no API key provisioned yet.
            set({
              isLoaded: true,
              staffProfile: profile,
              profileError: 'no_key',
              available: [],
              selected: null,
            })
            return
          }

          const op: Operator = {
            consumer_id: profile.consumer_id,
            display_name: profile.name ?? profile.email,
            initials: initials(profile.name ?? profile.email),
          }

          // Reconcile with any persisted selection: if the persisted
          // consumer_id matches the profile's consumer_id, keep it.
          // Otherwise clear so the splash card is shown.
          //
          // Browser: the signed-in Google identity IS the operator, so select
          // it directly rather than asking the operator to confirm themselves.
          const persistedId = get().selected?.consumer_id
          const reconciled =
            !IS_TAURI || persistedId === profile.consumer_id ? op : null

          set({
            available: [op],
            isLoaded: true,
            staffProfile: profile,
            profileError: null,
            selected: reconciled,
          })
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err)
          if (message === 'staff_not_found') {
            set({
              isLoaded: true,
              profileError: 'no_account',
              available: [],
              selected: null,
            })
            return
          }
          // Network or command error — surface as loadError so the retry
          // panel renders.
          set({
            isLoaded: true,
            loadError: message,
            available: [],
            selected: null,
          })
        }
      },

      loadOperators: async (options) => {
        const force = options?.force ?? false
        if (!force && get().isLoaded) return

        set({ loadError: null })

        try {
          const operators = await listOperators()
          // Reconcile: if a persisted consumer_id is still valid, hydrate the
          // full Operator object from the freshly loaded list. If it's no
          // longer present, drop the selection so the splash renders.
          const persistedId = get().selected?.consumer_id
          const reconciled = persistedId
            ? operators.find((op) => op.consumer_id === persistedId) ?? null
            : null
          set({
            available: operators,
            isLoaded: true,
            selected: reconciled,
          })
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err)
          set({
            available: [],
            isLoaded: true,
            loadError: message,
            selected: null,
          })
        }
      },

      selectOperator: (op) => {
        set({ selected: op })
      },

      clearOperator: () => {
        // Reset scopes and profile state so they are re-fetched on next sign-in.
        set({
          selected: null,
          scopes: [],
          scopesLoaded: false,
          isLoaded: false,
          staffProfile: null,
          profileError: null,
          loadError: null,
        })
      },

      loadScopes: async (options) => {
        const force = options?.force ?? false
        if (!force && get().scopesLoaded) return

        try {
          const response = await api.get<{ object: string; data: string[] }>('/v1/me/scopes')
          const data = Array.isArray(response?.data) ? response.data : []
          set({ scopes: data, scopesLoaded: true })
        } catch (err) {
          // 404 means the /v1/me/scopes endpoint is not yet deployed on this
          // environment. Fail open with wildcard so operators are not locked
          // out while the backend rolls out. Any other error (401, 403, network)
          // keeps the safe fail-closed behaviour (empty scopes = locked UI).
          if (err instanceof ApiClientError && err.statusCode === 404) {
            set({ scopes: ['*'], scopesLoaded: true })
            return
          }
          set({ scopesLoaded: true })
        }
      },

      hasScope: (required) => {
        return checkScope(get().scopes, required)
      },
    }),
    {
      name: 'stream-operator',
      // Persist only the consumer_id — the full Operator is re-hydrated from
      // `available` after `loadOperators()` resolves.
      partialize: (state) => ({
        selected: state.selected
          ? { consumer_id: state.selected.consumer_id }
          : null,
      }),
      // The persisted slice is a stub Operator with only consumer_id set;
      // mark `isLoaded` and `available` as needing a fresh load.
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<OperatorState> | undefined
        const persistedId =
          persisted &&
          typeof persisted.selected === 'object' &&
          persisted.selected !== null &&
          'consumer_id' in persisted.selected &&
          typeof (persisted.selected as { consumer_id?: unknown }).consumer_id ===
            'string'
            ? (persisted.selected as { consumer_id: string }).consumer_id
            : null

        return {
          ...currentState,
          selected: persistedId
            ? // Stub Operator until `loadOperators()` reconciles. The bootstrap
              // never reads display_name/initials before reconciliation, but
              // we provide harmless defaults to keep the type honest.
              {
                consumer_id: persistedId,
                display_name: '',
                initials: '',
              }
            : null,
        }
      },
    },
  ),
)
